import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import {
  APPOINTMENT_STATUS_LABELS,
  APPOINTMENT_STEP_ACTIONS,
  nextAppointmentStatus,
  type AppointmentStatus,
  type AppointmentView,
} from "@shared/modules/x-atelier";
import { Button } from "../../../core/components/ui/button";
import { Icon } from "../../../core/components/ui/icon";
import { useToastStore } from "../../../core/stores/toast";
import { listAppointments, setAppointmentStatus } from "../lib/api";
import {
  addDays,
  dayKey,
  formatDayLong,
  formatDayShort,
  formatHour,
  startOfDay,
} from "../lib/dates";
import { StatusBadge } from "./status-badge";
import { QuickActions } from "./quick-actions";
import { BookingChannelBadge, NewClientBadge, PresenceBadge } from "./booking-badges";

type State = { kind: "loading" } | { kind: "failed" } | { kind: "ready" };

const IN_SHOP: AppointmentStatus[] = ["vehicule_recu", "en_cours", "termine"];
const EXPECTED: AppointmentStatus[] = ["reserve", "confirme"];

/**
 * « Ma journée » : ce que l'atelier a devant lui, en trois paquets — les
 * véhicules déjà dans l'atelier, ceux qu'on attend, ceux qui attendent leur
 * propriétaire. Chaque rangée porte l'action qui fait avancer le travail
 * (« Véhicule reçu », « Commencer », « Terminé »…) : un tap, sans ouvrir la fiche.
 */
export function TodayWidget() {
  const push = useToastStore((s) => s.push);
  const [state, setState] = useState<State>({ kind: "loading" });
  const [today, setToday] = useState<AppointmentView[]>([]);
  const [ready, setReady] = useState<AppointmentView[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    const from = startOfDay(new Date());
    try {
      const [day, prets] = await Promise.all([
        listAppointments({ from: from.toISOString(), to: addDays(from, 1).toISOString() }),
        listAppointments({ status: ["pret"], limit: 50 }),
      ]);
      setToday(day);
      setReady(prets);
      setState({ kind: "ready" });
    } catch {
      setState({ kind: "failed" });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const groups = useMemo(() => {
    const inShop = today.filter((a) => IN_SHOP.includes(a.status));
    const expected = today.filter((a) => EXPECTED.includes(a.status));
    const seen = new Set(today.map((a) => a.id));
    const pickup = [
      ...today.filter((a) => a.status === "pret"),
      ...ready.filter((a) => !seen.has(a.id)),
    ];
    return { inShop, expected, pickup };
  }, [today, ready]);

  const advance = async (a: AppointmentView) => {
    const next = nextAppointmentStatus(a.status);
    if (!next || busyId) return;
    setBusyId(a.id);
    try {
      await setAppointmentStatus(a.id, next);
      push("success", `${a.title} — ${APPOINTMENT_STATUS_LABELS[next]}`);
      await load();
    } catch {
      push("danger", "Changement de statut impossible");
    } finally {
      setBusyId(null);
    }
  };

  const total = today.filter((a) => !["annule", "non_presente"].includes(a.status)).length;

  return (
    <section className="card today">
      <header className="today__head">
        <div>
          <h2 className="today__title">Ma journée</h2>
          <p className="today__date">{formatDayLong(new Date())}</p>
        </div>
        <div className="today__kpis" aria-label="Résumé du jour">
          <span className="today__kpi">
            <strong>{total}</strong> RDV
          </span>
          <span className="today__kpi">
            <strong>{groups.inShop.length}</strong> à l'atelier
          </span>
          <span className="today__kpi">
            <strong>{groups.pickup.length}</strong> à récupérer
          </span>
        </div>
        <Link
          to={`/agenda?vue=agenda&date=${dayKey(new Date())}`}
          className="btn btn--ghost btn--sm"
        >
          Ouvrir l'agenda
        </Link>
      </header>

      {state.kind === "loading" ? (
        <p className="atelier-widget__empty">Chargement…</p>
      ) : state.kind === "failed" ? (
        <p className="atelier-widget__empty">Agenda indisponible pour l'instant.</p>
      ) : total === 0 && groups.pickup.length === 0 ? (
        <p className="atelier-widget__empty">Aucun rendez-vous aujourd'hui. Bonne journée !</p>
      ) : (
        <div className="today__groups">
          <TodayGroup
            title="À l'atelier"
            icon="wrench"
            items={groups.inShop}
            empty="Aucun véhicule dans l'atelier pour l'instant."
            busyId={busyId}
            onAdvance={advance}
          />
          <TodayGroup
            title="Arrivées prévues"
            icon="calendar"
            items={groups.expected}
            empty="Plus aucune arrivée prévue aujourd'hui."
            busyId={busyId}
            onAdvance={advance}
          />
          <TodayGroup
            title="Prêts à récupérer"
            icon="flag"
            items={groups.pickup}
            empty="Aucun véhicule n'attend son propriétaire."
            busyId={busyId}
            onAdvance={advance}
            showDate
          />
        </div>
      )}
    </section>
  );
}

function TodayGroup({
  title,
  icon,
  items,
  empty,
  busyId,
  onAdvance,
  showDate = false,
}: {
  title: string;
  icon: "wrench" | "calendar" | "flag";
  items: AppointmentView[];
  empty: string;
  busyId: string | null;
  onAdvance: (a: AppointmentView) => void;
  showDate?: boolean;
}) {
  return (
    <div className="today__group">
      <h3 className="today__group-title">
        <Icon name={icon} size={16} />
        {title}
        <span className="today__group-count">{items.length}</span>
      </h3>
      {items.length === 0 ? (
        <p className="today__empty">{empty}</p>
      ) : (
        <ul className="today__list">
          {items.map((a) => {
            const next = nextAppointmentStatus(a.status);
            return (
              <li
                key={a.id}
                className="today__item"
                style={
                  a.technicianColor
                    ? ({ "--tech-color": a.technicianColor } as CSSProperties)
                    : undefined
                }
              >
                <Link to={`/travaux/${a.id}`} className="today__item-main">
                  <span className="today__item-time">
                    {showDate ? formatDayShort(a.startAt) : formatHour(a.startAt)}
                  </span>
                  <span className="today__item-text">
                    <span className="today__item-title">{a.title}</span>
                    <span className="today__item-sub">
                      {a.clientName}
                      {a.technicianName ? ` · ${a.technicianName}` : ""}{" "}
                      <BookingChannelBadge channel={a.bookingChannel} compact />
                      <PresenceBadge at={a.presenceConfirmedAt} compact />
                      <NewClientBadge status={a.clientStatus} compact />
                    </span>
                  </span>
                  <StatusBadge status={a.status} />
                </Link>
                <span className="today__item-actions">
                  <QuickActions phone={a.clientPhone} size="sm" />
                  {next ? (
                    <Button
                      variant={next === "recupere" || next === "termine" ? "success" : "ghost"}
                      size="sm"
                      disabled={busyId !== null}
                      icon={<Icon name={next === "en_cours" ? "play" : "check"} size={14} />}
                      onClick={() => onAdvance(a)}
                    >
                      {APPOINTMENT_STEP_ACTIONS[next]}
                    </Button>
                  ) : null}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
